import { readEpoch, type V1Result } from './client';
import type { ApiProblem } from './problem';
import { OPENING_MS } from './sharedReads';

/**
 * Whether a screen read failed because its route doesn't exist, as on a backend rolled back to before it: a 404 that
 * isn't a v1 problem with a code (a route that exists answers a missing row with one, `not_found`). The screen then
 * reads its parts one by one, as it did before the screen read existed.
 */
export const routeMissing = (error: ApiProblem | null | undefined): boolean => !!error && error.status === 404 && error.code === 'http_404';

/**
 * A screen's one read (getStoryWorkspace and the like), kept for the parts of the screen that show pieces of it and
 * read as they mount: each part takes the answer once, for its first read, while nothing has been written and nothing
 * has changed elsewhere since the read was sent, for OPENING_MS at most. A part's later reads (a refresh, a retry) are
 * its own reads.
 */
export class ScreenRead<T> {
  private held: { key: string; at: number; epoch: number; answer: Promise<V1Result<T>>; taken: Set<string> } | null = null;

  /** Reads the screen for `key` (the story, the project), keeping the answer for the parts that take it. */
  read(key: string, read: () => Promise<V1Result<T>>): Promise<V1Result<T>> {
    const epoch = readEpoch();
    const answer = read();
    this.held = { key, at: Date.now(), epoch, answer, taken: new Set() };
    return answer;
  }

  /** The screen's answer for `part`'s first read of `key`; null when the part reads on its own. */
  take(key: string, part: string): Promise<V1Result<T>> | null {
    const held = this.held;
    if (!held || held.key !== key || held.taken.has(part)) return null;
    if (Date.now() - held.at >= OPENING_MS || held.epoch !== readEpoch()) return null;
    held.taken.add(part);
    return held.answer;
  }
}
